import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZE = 15;

/**
 * Client-side pagination + optional search filter.
 * Returns everything a paginated table needs:
 *   items:    the current page's items
 *   page, setPage, totalPages
 *   query, setQuery   (only when searchFields provided)
 *   totalFiltered / totalAll
 * Automatically resets the page when the query changes.
 */
export function usePaginatedList(all, { pageSize = PAGE_SIZE, searchFields = null } = {}) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!searchFields || !query.trim()) return all;
    const q = query.trim().toLowerCase();
    return all.filter((row) =>
      searchFields.some((f) => {
        const v = row[f];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [all, query, searchFields]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  // Reset to page 1 when the filter changes or the list shrinks below current page
  useEffect(() => { setPage(1); }, [query]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const startIdx = (page - 1) * pageSize;
  const items = filtered.slice(startIdx, startIdx + pageSize);

  return {
    items, page, setPage, totalPages, pageSize,
    query, setQuery,
    totalFiltered: filtered.length, totalAll: all.length,
    rangeStart: filtered.length ? startIdx + 1 : 0,
    rangeEnd: startIdx + items.length,
  };
}

export function Pagination({ page, totalPages, setPage, rangeStart, rangeEnd, totalFiltered, testId = "pagination" }) {
  if (totalPages <= 1 && totalFiltered <= 0) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-500 dark:text-slate-400" data-testid={testId}>
      <div>
        {totalFiltered === 0
          ? "No results"
          : <>Showing <span className="font-semibold text-slate-700 dark:text-slate-300">{rangeStart}–{rangeEnd}</span> of <span className="font-semibold text-slate-700 dark:text-slate-300">{totalFiltered}</span></>}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          data-testid={`${testId}-prev`}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="font-mono text-xs px-2" data-testid={`${testId}-info`}>
          Page {page} of {totalPages}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          data-testid={`${testId}-next`}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
